var _react_refresh_temp_1, _react_refresh_temp_2, _react_refresh_temp_3, _react_refresh_temp_4;
var _react_refresh_temp_5;
_react_refresh_temp_5 = $RefreshSig$();
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';
import { store, useAppDispatch, useAppSelector, } from '../../../store';
import { setWebcamPaginating } from '../../../store/slices/sessionSlice';
import { UserDeviceType } from '../../../store/slices/interfaces/session';
import { setForMobileAndTablet, setForMobileLandscape, setForPC, } from './helpers/utils';
import { ColumnCameraPosition } from '../../../store/slices/interfaces/roomSettings';
const MOBILE_PER_PAGE = 6, TABLET_PER_PAGE = 9, DESKTOP_PER_PAGE = 24, VERTICAL_PER_PAGE = 3, VERTICAL_TOP_BOTTOM_PER_PAGE = 8, VERTICAL_TABLET_PORTRAIT = 5;
const screenWidthSelector = createSelector(_react_refresh_temp_1 = (state) => state.bottomIconsActivity, (bottomIconsActivity) => bottomIconsActivity.screenWidth);
$RefreshReg$(_react_refresh_temp_1, "screenWidthSelector$createSelector");
const deviceOrientationSelector = createSelector(_react_refresh_temp_2 = (state) => state.bottomIconsActivity, (bottomIconsActivity) => bottomIconsActivity.deviceOrientation);
$RefreshReg$(_react_refresh_temp_2, "deviceOrientationSelector$createSelector");
const columnCameraPositionSelector = createSelector(_react_refresh_temp_3 = (state) => state.roomSettings, (roomSettings) => roomSettings.columnCameraPosition);
$RefreshReg$(_react_refresh_temp_3, "columnCameraPositionSelector$createSelector");
const VideosComponentElms = ({ allParticipants, totalNumWebcams, isVertical, }) => {
    _react_refresh_temp_5();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const screenWidth = useAppSelector(screenWidthSelector);
    const deviceOrientation = useAppSelector(deviceOrientationSelector);
    const columnCameraPosition = useAppSelector(columnCameraPositionSelector);
    const deviceType = store.getState().session.userDeviceType;
    const [participantsToRender, setParticipantsToRender] = useState([]);
    const [webcamPerPage, setWebcamPerPage] = useState(isVertical ? VERTICAL_PER_PAGE : DESKTOP_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(0);
    const [showPre, setShowPre] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    useEffect(() => {
        if (isVertical) {
            if (deviceType === UserDeviceType.DESKTOP && screenWidth > 1024) {
                if (columnCameraPosition === ColumnCameraPosition.TOP ||
                    columnCameraPosition === ColumnCameraPosition.BOTTOM) {
                    setWebcamPerPage(VERTICAL_TOP_BOTTOM_PER_PAGE);
                    return;
                }
            }
            else if (deviceType === UserDeviceType.TABLET &&
                deviceOrientation === 'portrait') {
                setWebcamPerPage(VERTICAL_TABLET_PORTRAIT);
                return;
            }
            setWebcamPerPage(VERTICAL_PER_PAGE);
            return;
        }
        if (!screenWidth) {
            return;
        }
        if (screenWidth <= 640) {
            setWebcamPerPage(MOBILE_PER_PAGE);
            setIsSmallScreen(true);
        }
        else if (screenWidth > 640 && screenWidth <= 1024) {
            if (deviceType === UserDeviceType.MOBILE) {
                setWebcamPerPage(MOBILE_PER_PAGE);
            }
            else {
                setWebcamPerPage(TABLET_PER_PAGE);
            }
            setIsSmallScreen(true);
        }
        else {
            if (deviceType === UserDeviceType.TABLET) {
                setWebcamPerPage(TABLET_PER_PAGE);
                setIsSmallScreen(true);
            }
            else {
                setWebcamPerPage(DESKTOP_PER_PAGE);
                setIsSmallScreen(false);
            }
        }
    }, [isVertical, screenWidth, columnCameraPosition, deviceOrientation]);
    const setParticipantsToDisplay = ([...allParticipants], page_number, per_page) => {
        const display = allParticipants.slice((page_number - 1) * per_page, page_number * per_page);
        setParticipantsToRender(display);
        if (page_number === 1) {
            dispatch(setWebcamPaginating(false));
        }
        else {
            dispatch(setWebcamPaginating(true));
        }
    };
    useEffect(() => {
        if (allParticipants.length <= webcamPerPage) {
            // we don't need any pagination
            setParticipantsToRender(allParticipants);
        }
        else {
            setCurrentPage(1);
            setParticipantsToDisplay(allParticipants, 1, webcamPerPage);
        }
    }, [allParticipants, webcamPerPage]);
    useEffect(() => {
        if (totalNumWebcams > webcamPerPage) {
            if (currentPage === 1) {
                setShowPre(false);
            }
            else {
                setShowPre(true);
            }
            if (currentPage >= totalNumWebcams / webcamPerPage) {
                setShowNext(false);
            }
            else {
                setShowNext(true);
            }
        }
        else {
            setShowPre(false);
            setShowNext(false);
        }
    }, [totalNumWebcams, currentPage, webcamPerPage]);
    const prePage = () => {
        setParticipantsToDisplay(allParticipants, currentPage - 1, webcamPerPage);
        setCurrentPage(currentPage - 1);
    };
    const nextPage = () => {
        setParticipantsToDisplay(allParticipants, currentPage + 1, webcamPerPage);
        setCurrentPage(currentPage + 1);
    };
    const videoParticipantsElms = useMemo(() => {
        if (isVertical) {
            return participantsToRender;
        }
        let elms;
        if (deviceType === UserDeviceType.MOBILE &&
            deviceOrientation === 'landscape') {
            elms = setForMobileLandscape(participantsToRender);
        }
        else if (deviceType === UserDeviceType.MOBILE ||
            deviceType === UserDeviceType.TABLET ||
            isSmallScreen) {
            // for mobile & tablet
            // at present we can use same logic for small screen of desktop
            elms = setForMobileAndTablet(participantsToRender);
        }
        else {
            // for PC
            elms = setForPC(participantsToRender);
        }
        return elms;
    }, [isVertical, participantsToRender, deviceOrientation]);
    const render = () => {
        return (_jsxs(_Fragment, { children: [showPre ? (_jsx("button", { type: "button", className: "previous-cam", onClick: () => prePage(), children: _jsx("i", { className: "pnm-arrow-up" }) })) : null, _jsx(_Fragment, { children: videoParticipantsElms }), showNext ? (_jsx("button", { type: "button", className: "next-cam", onClick: () => nextPage(), children: _jsx("i", { className: "pnm-arrow-down" }) })) : null] }));
    };
    return (_jsxs(_Fragment, { children: [totalNumWebcams > 0 ? (_jsx("div", { className: `all-webcam-wrapper total-cam-${totalNumWebcams} selected-cam-${webcamPerPage} page-${currentPage} ${isVertical ? 'vertical-webcams' : ''}`, children: _jsx("div", { className: "all-webcam-wrapper-inner", children: render() }) })) : null, deviceType === UserDeviceType.DESKTOP &&
                totalNumWebcams > 6 &&
                !isVertical ? (_jsxs("div", { className: "select-camera-number", children: [_jsxs("select", { name: "select-camera-num", id: "select-camera-num", defaultValue: "24", onChange: (e) => setWebcamPerPage(Number(e.currentTarget.value)), children: [_jsx("option", { value: "6", children: "6" }), _jsx("option", { value: "8", children: "8" }), _jsx("option", { value: "12", children: "12" }), _jsx("option", { value: "15", children: "15" }), _jsx("option", { value: "18", children: "18" }), _jsx("option", { value: "24", children: "24" })] }), _jsx("label", { htmlFor: "select-camera-num", children: t('app.webcams-per-page') })] })) : null] }));
};
_react_refresh_temp_4 = VideosComponentElms;
$RefreshReg$(_react_refresh_temp_4, "VideosComponentElms");
_react_refresh_temp_5(VideosComponentElms, "rvsx8syGYxXKVzkLz4B06iLu7lU=", false, () => [useAppDispatch, useTranslation, useAppSelector, useAppSelector, useAppSelector]);
export default VideosComponentElms;
